/**
 * Permissions Configuration
 * 
 * Central configuration for role-based access control.
 * Defines user roles, route access, and feature permissions.
 */

// User Role Constants
export const ROLES = {
  USER: 'user',
  ADMIN: 'admin',
  DEVELOPER: 'developer',
};

// Feature/Permission Constants
export const PERMISSIONS = {
  // Page Access
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_REPAIRS: 'view_repairs',
  VIEW_MAP: 'view_map',
  VIEW_ASSETS: 'view_assets',
  VIEW_ADMIN: 'view_admin',
  VIEW_SETTINGS: 'view_settings',
  
  // Organization Management
  MANAGE_ALL_ORGANIZATIONS: 'manage_all_organizations',
  MANAGE_OWN_ORGANIZATIONS: 'manage_own_organizations',
  ADD_ORGANIZATION: 'add_organization',
  
  // User Management
  VIEW_USERS: 'view_users',
  MANAGE_USERS: 'manage_users',
  
  // File Management
  VIEW_FILES: 'view_files',
  MANAGE_FILES: 'manage_files',
  
  // Repair Management
  VIEW_ALL_REPAIRS: 'view_all_repairs',
  VIEW_OWN_REPAIRS: 'view_own_repairs',
  CREATE_REPAIR: 'create_repair',
  MANAGE_REPAIRS: 'manage_repairs',
  
  // Admin-only Features
  ADMIN_REPORTING: 'admin_reporting',
  SYSTEM_SETTINGS: 'system_settings',
};

// Permission Matrix: Define which roles have which permissions
const PERMISSION_MATRIX = {
  [ROLES.USER]: [
    // Standard user access - most features except admin functions
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_REPAIRS,
    PERMISSIONS.VIEW_MAP,
    PERMISSIONS.VIEW_ASSETS,
    PERMISSIONS.VIEW_SETTINGS,
    PERMISSIONS.MANAGE_OWN_ORGANIZATIONS,
    PERMISSIONS.ADD_ORGANIZATION,
    PERMISSIONS.VIEW_ALL_REPAIRS,
    PERMISSIONS.VIEW_OWN_REPAIRS,
    PERMISSIONS.CREATE_REPAIR,
    PERMISSIONS.MANAGE_REPAIRS,
  ],
  
  [ROLES.ADMIN]: [
    // Full access to everything within organization
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_REPAIRS,
    PERMISSIONS.VIEW_MAP,
    PERMISSIONS.VIEW_ASSETS,
    PERMISSIONS.VIEW_ADMIN,
    PERMISSIONS.VIEW_SETTINGS,
    PERMISSIONS.MANAGE_ALL_ORGANIZATIONS,
    PERMISSIONS.MANAGE_OWN_ORGANIZATIONS,
    PERMISSIONS.ADD_ORGANIZATION,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_FILES,
    PERMISSIONS.MANAGE_FILES,
    PERMISSIONS.VIEW_ALL_REPAIRS,
    PERMISSIONS.VIEW_OWN_REPAIRS,
    PERMISSIONS.CREATE_REPAIR,
    PERMISSIONS.MANAGE_REPAIRS,
    PERMISSIONS.ADMIN_REPORTING,
    PERMISSIONS.SYSTEM_SETTINGS,
  ],
  
  [ROLES.DEVELOPER]: [
    // Developer has access to ALL features (system-wide)
    // This is handled by is_developer flag in backend, but included here for completeness
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_REPAIRS,
    PERMISSIONS.VIEW_MAP,
    PERMISSIONS.VIEW_ASSETS,
    PERMISSIONS.VIEW_ADMIN,
    PERMISSIONS.VIEW_SETTINGS,
    PERMISSIONS.MANAGE_ALL_ORGANIZATIONS,
    PERMISSIONS.MANAGE_OWN_ORGANIZATIONS,
    PERMISSIONS.ADD_ORGANIZATION,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_FILES,
    PERMISSIONS.MANAGE_FILES,
    PERMISSIONS.VIEW_ALL_REPAIRS,
    PERMISSIONS.VIEW_OWN_REPAIRS,
    PERMISSIONS.CREATE_REPAIR,
    PERMISSIONS.MANAGE_REPAIRS,
    PERMISSIONS.ADMIN_REPORTING,
    PERMISSIONS.SYSTEM_SETTINGS,
  ],
};

// Route Configuration: Define which routes are accessible by which roles
export const ROUTE_ACCESS = {
  '/': [ROLES.USER, ROLES.ADMIN, ROLES.DEVELOPER], // Dashboard (varies by role)
  '/map': [ROLES.USER, ROLES.ADMIN, ROLES.DEVELOPER],
  '/assets': [ROLES.USER, ROLES.ADMIN, ROLES.DEVELOPER],
  '/repairs': [ROLES.USER, ROLES.ADMIN, ROLES.DEVELOPER],
  '/admin': [ROLES.ADMIN, ROLES.DEVELOPER],
  '/settings': [ROLES.USER, ROLES.ADMIN, ROLES.DEVELOPER],
};

// Navigation Items Configuration
export const getNavigationItems = (role) => {
  const baseItems = [];
  
  // Dashboard - Everyone gets this (but content varies)
  baseItems.push({ path: '/', label: 'Dashboard', icon: 'dashboard' });
  
  // Repairs - Everyone gets this
  baseItems.push({ path: '/repairs', label: 'Repairs', icon: 'repairs' });
  
  // Role-specific navigation
  // Map and Assets - All roles can access
  baseItems.push({ path: '/map', label: 'Map', icon: 'map' });
  baseItems.push({ path: '/assets', label: 'Asset Manager', icon: 'assets' });
  
  // Admin page - Admin and Developer only
  if (role === ROLES.ADMIN || role === ROLES.DEVELOPER) {
    baseItems.push({ path: '/admin', label: 'Admin', icon: 'admin' });
  }
  
  return baseItems;
};

/**
 * Check if a role has a specific permission
 * @param {string} role - The user's role
 * @param {string} permission - The permission to check
 * @returns {boolean}
 */
export const hasPermission = (role, permission) => {
  if (!role) return false;
  const normalizedRole = role.toLowerCase();
  const permissions = PERMISSION_MATRIX[normalizedRole] || [];
  return permissions.includes(permission);
};

/**
 * Check if a role can access a specific route
 * @param {string} role - The user's role
 * @param {string} route - The route path
 * @returns {boolean}
 */
export const canAccessRoute = (role, route) => {
  if (!role) return false;
  const normalizedRole = role.toLowerCase();
  const allowedRoles = ROUTE_ACCESS[route];
  if (!allowedRoles) return true; // If route not defined, allow by default
  return allowedRoles.includes(normalizedRole);
};

/**
 * Check if user is admin (full access within organization)
 * @param {string} role 
 * @returns {boolean}
 */
export const isAdminRole = (role) => {
  if (!role) return false;
  const normalizedRole = role.toLowerCase();
  return normalizedRole === ROLES.ADMIN;
};

/**
 * Check if user is developer (system-wide access)
 * @param {string} role 
 * @returns {boolean}
 */
export const isDeveloperRole = (role) => {
  if (!role) return false;
  const normalizedRole = role.toLowerCase();
  return normalizedRole === ROLES.DEVELOPER;
};

/**
 * Check if user is a regular user
 * @param {string} role 
 * @returns {boolean}
 */
export const isUserRole = (role) => {
  if (!role) return false;
  const normalizedRole = role.toLowerCase();
  return normalizedRole === ROLES.USER;
};

/**
 * Get the appropriate dashboard type for a role
 * @param {string} role 
 * @returns {'admin' | 'user' | 'developer'}
 */
export const getDashboardType = (role) => {
  if (isDeveloperRole(role)) return 'developer';
  if (isAdminRole(role)) return 'admin';
  return 'user'; // Default for user role
};

/**
 * Get the appropriate admin/settings page type for a role
 * @param {string} role 
 * @returns {'admin' | 'settings' | null}
 */
export const getAdminPageType = (role) => {
  if (isAdminRole(role) || isDeveloperRole(role)) return 'admin';
  if (isUserRole(role)) return 'settings';
  return null;
};

const permissionsModule = {
  ROLES,
  PERMISSIONS,
  ROUTE_ACCESS,
  getNavigationItems,
  hasPermission,
  canAccessRoute,
  isAdminRole,
  isDeveloperRole,
  isUserRole,
  getDashboardType,
  getAdminPageType,
};

export default permissionsModule;

